import { useState } from 'react'
import useSettingsStore, { DEFAULT_SETTINGS } from '../store/settingsStore'
import { useCritterStore } from '../store/critterStore'
import CreateCritterPanel from './CreateCritterPanel'
import UploadSpritesheetPanel from './UploadSpritesheetPanel'
import { get2DTypes, is2DType } from '../utils/critterTypeHelpers'
import '../styles/SettingsPanel.css'
import '../styles/Settings3DPanel.css'

const SettingsPanel = ({ isAdmin = false, broadcasterLogin = null }) => {
  const {
    settings,
    isPanelOpen,
    togglePanel,
    closePanel,
    toggle3DPanel,
    updateSetting,
    resetToDefaults,
    saveToApi,
    hasChanges,
    isLoading,
    is3DPanelOpen
  } = useSettingsStore()

  const { clearAllCritters, critters, critterTypes, loadCritterTypes } = useCritterStore()
  const [activeTab, setActiveTab] = useState('movement')
  const [saveStatus, setSaveStatus] = useState(null)
  const [isCreateCritterOpen, setIsCreateCritterOpen] = useState(false)
  const [isSpritesheetPanelOpen, setIsSpritesheetPanelOpen] = useState(false)

  // Admin actions
  const [adminUsername, setAdminUsername] = useState('')
  const [adminCritterType, setAdminCritterType] = useState('cat')

  const handleSave = async () => {
    const success = await saveToApi()
    setSaveStatus(success ? 'saved' : 'error')
    setTimeout(() => setSaveStatus(null), 2000)
  }

  const handleAddCritter = () => {
    if (adminUsername.trim()) {
      const { addCritter } = useCritterStore.getState()
      // Normalize username to lowercase to match Twitch's format
      addCritter(`admin_${Date.now()}`, adminUsername.trim().toLowerCase(), adminCritterType)
      setAdminUsername('')
    }
  }

  const handleRemoveCritter = (userId) => {
    const { removeCritter } = useCritterStore.getState()
    removeCritter(userId)
  }

  const handleClearAll = () => {
    if (confirm('Are you sure you want to remove all critters?')) {
      clearAllCritters()
    }
  }

  if (!isPanelOpen && !is3DPanelOpen) {
    return (
      <div className="settings-toggle-group">
        <button
          className="settings-toggle-btn"
          onClick={togglePanel}
          title="2D Overlay Settings"
        >
          <span>🎨</span> 2D Settings
        </button>
        <button
          className="settings-toggle-btn toggle-btn-3d"
          onClick={toggle3DPanel}
          title="3D Model Settings"
        >
          <span>🧊</span> 3D Settings
        </button>
      </div>
    )
  }

  if (!isPanelOpen) return null

  const critterList = Object.values(critters)
  const twoDCritterList = critterList.filter((c) => is2DType(critterTypes[c.critterType]))

  return (
    <div className="settings-overlay">
      <div className="settings-panel">
        <div className="settings-header">
          <h2>2D Overlay Settings</h2>
          <button className="close-btn" onClick={closePanel}>×</button>
        </div>

        <div className="settings-tabs">
          <button
            className={`tab ${activeTab === 'movement' ? 'active' : ''}`}
            onClick={() => setActiveTab('movement')}
          >
            Movement
          </button>
          <button
            className={`tab ${activeTab === 'visual' ? 'active' : ''}`}
            onClick={() => setActiveTab('visual')}
          >
            Visual
          </button>
          <button
            className={`tab ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            Features
          </button>
          <button
            className={`tab ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            Permissions
          </button>
          {isAdmin && (
            <button
              className={`tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
            >
              Admin
            </button>
          )}
          <button
            className={`tab ${activeTab === 'help' ? 'active' : ''}`}
            onClick={() => setActiveTab('help')}
          >
            Help
          </button>
        </div>

        <div className="settings-content">
          {/* Movement Tab */}
          {activeTab === 'movement' && (
            <div className="settings-section">
              <h3>Movement Settings</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.staticMode}
                    onChange={(e) => updateSetting('staticMode', e.target.checked)}
                  />
                  Static Mode
                </label>
                <span className="setting-hint">Critters stay in place (randomly positioned but don't move)</span>
              </div>

              <div className="setting-item">
                <label>
                  Roam Interval (ms)
                  <span className="setting-value">{settings.roamInterval}ms</span>
                </label>
                <input
                  type="range"
                  min="1000"
                  max="10000"
                  step="500"
                  value={settings.roamInterval}
                  onChange={(e) => updateSetting('roamInterval', parseInt(e.target.value))}
                />
                <span className="setting-hint">How often critters pick a new destination</span>
              </div>

              <div className="setting-item">
                <label>
                  Movement Speed
                  <span className="setting-value">{settings.roamSpeed.toFixed(2)}</span>
                </label>
                <input
                  type="range"
                  min="0.05"
                  max="0.5"
                  step="0.05"
                  value={settings.roamSpeed}
                  onChange={(e) => updateSetting('roamSpeed', parseFloat(e.target.value))}
                />
                <span className="setting-hint">Speed of critter movement (% per update)</span>
              </div>

              <div className="setting-item">
                <label>
                  Update Interval (ms)
                  <span className="setting-value">{settings.updateInterval}ms</span>
                </label>
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="50"
                  value={settings.updateInterval}
                  onChange={(e) => updateSetting('updateInterval', parseInt(e.target.value))}
                />
                <span className="setting-hint">Position update frequency (lower = smoother)</span>
              </div>

              <h3>Boundaries</h3>

              <div className="setting-row">
                <div className="setting-item half">
                  <label>
                    Min X
                    <span className="setting-value">{settings.minX}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={settings.minX}
                    onChange={(e) => updateSetting('minX', parseInt(e.target.value))}
                  />
                </div>
                <div className="setting-item half">
                  <label>
                    Max X
                    <span className="setting-value">{settings.maxX}%</span>
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="100"
                    value={settings.maxX}
                    onChange={(e) => updateSetting('maxX', parseInt(e.target.value))}
                  />
                </div>
              </div>

              <div className="setting-row">
                <div className="setting-item half">
                  <label>
                    Min Y
                    <span className="setting-value">{settings.minY}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={settings.minY}
                    onChange={(e) => updateSetting('minY', parseInt(e.target.value))}
                  />
                </div>
                <div className="setting-item half">
                  <label>
                    Max Y
                    <span className="setting-value">{settings.maxY}%</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={settings.maxY}
                    onChange={(e) => updateSetting('maxY', parseInt(e.target.value))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Visual Tab */}
          {activeTab === 'visual' && (
            <div className="settings-section">
              <h3>Visual Settings</h3>

              <div className="setting-item">
                <label>
                  Critter Scale
                  <span className="setting-value">{settings.critterScale.toFixed(1)}x</span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={settings.critterScale}
                  onChange={(e) => updateSetting('critterScale', parseFloat(e.target.value))}
                />
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.showUsernames}
                    onChange={(e) => updateSetting('showUsernames', e.target.checked)}
                  />
                  Show Usernames
                </label>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowChatBubbles}
                    onChange={(e) => updateSetting('allowChatBubbles', e.target.checked)}
                  />
                  Show Chat Bubbles
                </label>
              </div>

              <h3>Sprite Animations</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.enableAnimations}
                    onChange={(e) => updateSetting('enableAnimations', e.target.checked)}
                  />
                  Enable Animated Sprites
                </label>
                <span className="setting-hint">Use animated spritesheets instead of static images (requires spritesheet files)</span>
              </div>

              {settings.enableAnimations && (
                <div className="setting-item">
                  <label>
                    Animation FPS
                    <span className="setting-value">{settings.animationFps} fps</span>
                  </label>
                  <input
                    type="range"
                    min="6"
                    max="30"
                    step="1"
                    value={settings.animationFps}
                    onChange={(e) => updateSetting('animationFps', parseInt(e.target.value))}
                  />
                  <span className="setting-hint">Frames per second for sprite animations</span>
                </div>
              )}

              <h3>Animation Durations</h3>

              <div className="setting-item">
                <label>
                  Feed Duration (ms)
                  <span className="setting-value">{settings.feedDuration}ms</span>
                </label>
                <input
                  type="range"
                  min="500"
                  max="5000"
                  step="250"
                  value={settings.feedDuration}
                  onChange={(e) => updateSetting('feedDuration', parseInt(e.target.value))}
                />
              </div>

              <div className="setting-item">
                <label>
                  Pet Duration (ms)
                  <span className="setting-value">{settings.petDuration}ms</span>
                </label>
                <input
                  type="range"
                  min="250"
                  max="2000"
                  step="250"
                  value={settings.petDuration}
                  onChange={(e) => updateSetting('petDuration', parseInt(e.target.value))}
                />
              </div>

              <div className="setting-item">
                <label>
                  Chat Bubble Duration (ms)
                  <span className="setting-value">{settings.chatBubbleDuration}ms</span>
                </label>
                <input
                  type="range"
                  min="2000"
                  max="15000"
                  step="1000"
                  value={settings.chatBubbleDuration}
                  onChange={(e) => updateSetting('chatBubbleDuration', parseInt(e.target.value))}
                />
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="settings-section">
              <h3>Feature Toggles</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowFeeding}
                    onChange={(e) => updateSetting('allowFeeding', e.target.checked)}
                  />
                  Allow !feed Command
                </label>
                <span className="setting-hint">Users can feed their critters</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowPetting}
                    onChange={(e) => updateSetting('allowPetting', e.target.checked)}
                  />
                  Allow !pet Command
                </label>
                <span className="setting-hint">Users can pet their critters</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowDancing}
                    onChange={(e) => updateSetting('allowDancing', e.target.checked)}
                  />
                  Allow !dance Command
                </label>
                <span className="setting-hint">Users can make their critters dance with music notes</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowTricks}
                    onChange={(e) => updateSetting('allowTricks', e.target.checked)}
                  />
                  Allow !trick Command
                </label>
                <span className="setting-hint">Users can make their critters do a flip with sparkles</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowHugging}
                    onChange={(e) => updateSetting('allowHugging', e.target.checked)}
                  />
                  Allow !hug Command
                </label>
                <span className="setting-hint">Users can hug another viewer's critter</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowBonking}
                    onChange={(e) => updateSetting('allowBonking', e.target.checked)}
                  />
                  Allow !bonk Command
                </label>
                <span className="setting-hint">Users can playfully bonk another viewer's critter</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.allowCritterTypeChange}
                    onChange={(e) => updateSetting('allowCritterTypeChange', e.target.checked)}
                  />
                  Allow !crittertype Command
                </label>
                <span className="setting-hint">Users can change their critter type</span>
              </div>

              <h3>Sleep / Wake</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.enableSleepMode}
                    onChange={(e) => updateSetting('enableSleepMode', e.target.checked)}
                  />
                  Enable Sleep Mode
                </label>
                <span className="setting-hint">Inactive viewers' critters fall asleep (greyed out, static). They wake when the viewer chats again.</span>
              </div>

              {settings.enableSleepMode && (
                <div className="setting-item">
                  <label>
                    Sleep Timeout
                    <span className="setting-value">{Math.round(settings.sleepTimeout / 60000)} min</span>
                  </label>
                  <input
                    type="range"
                    min="60000"
                    max="1800000"
                    step="60000"
                    value={settings.sleepTimeout}
                    onChange={(e) => updateSetting('sleepTimeout', parseInt(e.target.value))}
                  />
                  <span className="setting-hint">How long before an inactive critter falls asleep</span>
                </div>
              )}

              <h3>Critter Limits</h3>

              <div className="setting-item">
                <label>
                  Max Critters
                  <span className="setting-value">{settings.maxCritters}</span>
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={settings.maxCritters}
                  onChange={(e) => updateSetting('maxCritters', parseInt(e.target.value))}
                />
              </div>

              <div className="setting-item">
                <label>Default Critter Type</label>
                <select
                  value={settings.defaultCritterType}
                  onChange={(e) => updateSetting('defaultCritterType', e.target.value)}
                >
                  {Object.entries(get2DTypes(critterTypes)).map(([key, type]) => (
                    <option key={key} value={key}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="settings-section">
              <h3>Subscription Requirements</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.requireSubscription}
                    onChange={(e) => updateSetting('requireSubscription', e.target.checked)}
                  />
                  Require Subscription
                </label>
                <span className="setting-hint">Only subscribers can create critters</span>
              </div>

              {settings.requireSubscription && (
                <div className="setting-item">
                  <label>Minimum Tier Required</label>
                  <select
                    value={settings.requiredTier}
                    onChange={(e) => updateSetting('requiredTier', parseInt(e.target.value))}
                  >
                    <option value={1000}>Tier 1</option>
                    <option value={2000}>Tier 2</option>
                    <option value={3000}>Tier 3</option>
                  </select>
                </div>
              )}

              <h3>Admin Permissions</h3>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.modCanUseAdminCommands}
                    onChange={(e) => updateSetting('modCanUseAdminCommands', e.target.checked)}
                  />
                  Mods Can Use Admin Commands
                </label>
                <span className="setting-hint">Allow moderators to add/remove critters</span>
              </div>

              <div className="setting-item checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={settings.broadcasterOnlyAdmin}
                    onChange={(e) => updateSetting('broadcasterOnlyAdmin', e.target.checked)}
                  />
                  Broadcaster-Only Admin
                </label>
                <span className="setting-hint">Only broadcaster can use admin commands</span>
              </div>
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === 'admin' && isAdmin && (
            <div className="settings-section">
              <h3>Quick Actions</h3>

              <div className="admin-actions">
                <div className="admin-action">
                  <h4>Add Critter</h4>
                  <div className="admin-form">
                    <input
                      type="text"
                      placeholder="Username"
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                    />
                    <select
                      value={adminCritterType}
                      onChange={(e) => setAdminCritterType(e.target.value)}
                    >
                      {Object.entries(get2DTypes(critterTypes)).map(([key, type]) => (
                        <option key={key} value={key}>{type.name}</option>
                      ))}
                    </select>
                    <button onClick={handleAddCritter}>Add</button>
                  </div>
                </div>

                <div className="admin-action">
                  <h4>Remove All Critters</h4>
                  <button className="danger-btn" onClick={handleClearAll}>
                    Clear All Critters
                  </button>
                </div>

                <div className="admin-action">
                  <h4>Custom Critter Types</h4>
                  <button
                    className="create-critter-btn"
                    onClick={() => setIsCreateCritterOpen(true)}
                  >
                    Create Custom Critter
                  </button>
                </div>

                <div className="admin-action">
                  <h4>Animated Spritesheets</h4>
                  <button
                    className="create-critter-btn"
                    onClick={() => setIsSpritesheetPanelOpen(true)}
                  >
                    Upload Spritesheets
                  </button>
                </div>

              </div>

              <h3>Active 2D Critters ({twoDCritterList.length})</h3>
              <div className="critter-list">
                {twoDCritterList.length === 0 ? (
                  <p className="empty-list">No active 2D critters</p>
                ) : (
                  twoDCritterList.map((critter) => (
                    <div key={critter.userId} className="critter-list-item">
                      <span className="critter-info">
                        <strong>{critter.username}</strong>
                        <span className="critter-type">({critter.critterType})</span>
                      </span>
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveCritter(critter.userId)}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Help Tab */}
          {activeTab === 'help' && (
            <div className="settings-section help-section">
              <h3>Quick Reference</h3>
              <div className="help-block">
                <div className="help-command"><code>!pet</code><span>Pet your critter (hearts)</span></div>
                <div className="help-command"><code>!feed</code><span>Feed your critter (food particles)</span></div>
                <div className="help-command"><code>!dance</code><span>Dance with music notes</span></div>
                <div className="help-command"><code>!trick</code><span>Flip with sparkle effects</span></div>
                <div className="help-command"><code>!hug @user</code><span>Hug another viewer's critter</span></div>
                <div className="help-command"><code>!bonk @user</code><span>Bonk another viewer's critter</span></div>
                <div className="help-command"><code>!crittertype [type]</code><span>Change your critter type</span></div>
                <div className="help-command"><code>!feast</code><span>Feed ALL critters (Admin)</span></div>
              </div>

              <div className="help-block" style={{textAlign: 'center', padding: '24px'}}>
                <p style={{marginBottom: '12px'}}>For complete documentation, settings guides, and troubleshooting:</p>
                <a
                  href="/guide"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    background: '#9147ff',
                    color: 'white',
                    padding: '10px 24px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}
                >
                  Open Full Documentation
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <button className="reset-btn" onClick={resetToDefaults}>
            Reset to Defaults
          </button>
          <div className="save-area">
            {saveStatus === 'saved' && <span className="save-success">Saved!</span>}
            {saveStatus === 'error' && <span className="save-error">Error saving</span>}
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={!hasChanges || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Create Critter Panel */}
      <CreateCritterPanel
        isOpen={isCreateCritterOpen}
        onClose={() => setIsCreateCritterOpen(false)}
        onTypeCreated={() => loadCritterTypes()}
        broadcasterLogin={broadcasterLogin}
      />

      {/* Upload Spritesheet Panel */}
      <UploadSpritesheetPanel
        isOpen={isSpritesheetPanelOpen}
        onClose={() => setIsSpritesheetPanelOpen(false)}
        broadcasterLogin={broadcasterLogin}
      />
    </div>
  )
}

export default SettingsPanel